import type { PropsWithChildren } from 'react'
import { createContext, useContext, useState, useMemo } from 'react'

import { PublishCampaignModal } from './PublishCampaign/PublishCampaignModal'
import { PublishCampaignSuccessModal } from './PublishCampaign/SuccessModal'
import { PublishCampaignFailureModal } from './PublishCampaign/FailureModal'
import { UnpublishCampaignModal } from './UnpublishCampaign/UnpublishCampaignModal'
import { UnpublishCampaignSuccessModal } from './UnpublishCampaign/SuccessModal'
import { UnpublishCampaignFailureModal } from './UnpublishCampaign/FailureModal'
import { DeleteCampaignModal } from './DeleteCampaign/DeleteCampaignModal'
import { DeleteCampaignFailureModal } from './DeleteCampaign/FailureModal'
import { DeleteCampaignSuccessModal } from './DeleteCampaign/SuccessModal'

type Err = Record<'code' | 'message' | 'title', string>
type Campaign = { id: string; title: string }

export type CampaignActionsDialogsContextType = Readonly<{
  publishCampaign: (campaign: Campaign) => void
  unpublishCampaign: (campaign: Campaign) => void
  deleteCampaign: (campaign: Campaign) => void
}>

const CampaignActionsDialogsContext = createContext<CampaignActionsDialogsContextType | null>(null)

export type CampaignActionsDialogsProviderProps = Readonly<{
  refresh: () => void
}>

export function CampaignActionsDialogsProvider({
  children,
  refresh
}: PropsWithChildren<CampaignActionsDialogsProviderProps>) {
  const [publishingCampaign, setPublishingCampaign] = useState<Campaign | null>(null)
  const [publishingCampaignSuccess, setPublishingCampaignSuccess] = useState<boolean>(false)
  const [publishingCampaignError, setPublishingCampaignError] = useState<Err | null>(null)

  const [unpublishingCampaign, setUnpublishingCampaign] = useState<Campaign | null>(null)
  const [unpublishingCampaignSuccess, setUnpublishingCampaignSuccess] = useState<boolean>(false)
  const [unpublishingCampaignError, setUnpublishingCampaignError] = useState<Err | null>(null)

  const [deletingCampaign, setDeletingCampaign] = useState<Campaign | null>(null)
  const [deletingCampaignSuccess, setDeletingCampaignSuccess] = useState<boolean>(false)
  const [deletingCampaignError, setDeletingCampaignError] = useState<Err | null>(null)

  const publishCampaign = (campaign: Campaign) => setPublishingCampaign(campaign)
  const onPublishCampaignSuccess = () => {
    setPublishingCampaign(null)
    setPublishingCampaignSuccess(true)
    refresh()
  }
  const onPublishCampaignError = (error?: Err) => {
    setPublishingCampaign(null)
    setPublishingCampaignError(error ?? null)
  }
  const closePublishDialog = () => setPublishingCampaign(null)

  const unpublishCampaign = (campaign: Campaign) => setUnpublishingCampaign(campaign)
  const onUnpublishCampaignSuccess = () => {
    setUnpublishingCampaign(null)
    setUnpublishingCampaignSuccess(true)
    refresh()
  }
  const onUnpublishCampaignError = (error?: Err) => {
    setUnpublishingCampaign(null)
    setUnpublishingCampaignError(error ?? null)
  }
  const closeUnpublishDialog = () => setUnpublishingCampaign(null)

  const deleteCampaign = (campaign: Campaign) => setDeletingCampaign(campaign)
  const onDeleteCampaignSuccess = () => {
    setDeletingCampaign(null)
    setDeletingCampaignSuccess(true)
    refresh()
  }
  const onDeleteCampaignError = (error?: Err) => {
    setDeletingCampaign(null)
    setDeletingCampaignError(error ?? null)
  }
  const closeDeleteDialog = () => setDeletingCampaign(null)

  const value = useMemo(
    () => ({
      publishCampaign,
      unpublishCampaign,
      deleteCampaign
    }),
    [publishCampaign, unpublishCampaign, deleteCampaign]
  )

  return (
    <CampaignActionsDialogsContext.Provider value={value}>
      {children}
      <PublishCampaignModal
        isOpen={!!publishingCampaign}
        handleClose={closePublishDialog}
        givingCampaign={publishingCampaign}
        onSuccess={onPublishCampaignSuccess}
        onError={onPublishCampaignError}
      />
      <PublishCampaignSuccessModal
        isOpen={publishingCampaignSuccess}
        handleClose={() => setPublishingCampaignSuccess(false)}
      />
      <PublishCampaignFailureModal
        isOpen={!!publishingCampaignError}
        handleClose={() => setPublishingCampaignError(null)}
        errorCode={publishingCampaignError?.code ?? ''}
      />
      <UnpublishCampaignModal
        isOpen={!!unpublishingCampaign}
        handleClose={closeUnpublishDialog}
        givingCampaign={unpublishingCampaign}
        onSuccess={onUnpublishCampaignSuccess}
        onError={onUnpublishCampaignError}
      />
      <UnpublishCampaignSuccessModal
        isOpen={unpublishingCampaignSuccess}
        handleClose={() => setUnpublishingCampaignSuccess(false)}
      />
      <UnpublishCampaignFailureModal
        isOpen={!!unpublishingCampaignError}
        handleClose={() => setUnpublishingCampaignError(null)}
        errorCode={unpublishingCampaignError?.code ?? ''}
      />
      <DeleteCampaignModal
        isOpen={!!deletingCampaign}
        handleClose={closeDeleteDialog}
        givingCampaign={deletingCampaign}
        onSuccess={onDeleteCampaignSuccess}
        onError={onDeleteCampaignError}
      />
      <DeleteCampaignSuccessModal
        isOpen={deletingCampaignSuccess}
        handleClose={() => setDeletingCampaignSuccess(false)}
      />
      <DeleteCampaignFailureModal isOpen={!!deletingCampaignError} handleClose={() => setDeletingCampaignError(null)} />
    </CampaignActionsDialogsContext.Provider>
  )
}

export const useCampaignActionsDialogs = () => {
  const context = useContext(CampaignActionsDialogsContext)

  if (!context) {
    throw new Error('useCampaignActionsDialogs must be used within a CampaignActionsDialogsProvider')
  }

  return context
}
